<template>
  <div class="confirm-buy-list-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Price Filters Card -->
    <CRow>
      <CCol>
        <CCard class="filter-card mb-3">
          <CCardHeader class="filter-header" @click="toggleFilters">
            <div class="d-flex justify-content-between align-items-center">
              <div class="header-content">
                <i class="fas fa-filter me-2 text-primary"></i>
                <strong>Price Filters</strong>
                <i
                  :class="filtersExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                  class="ms-2 text-muted"
                ></i>
              </div>
              <CBadge color="secondary" class="filter-count">
                {{ computedItems.length }} {{ computedItems.length === 1 ? 'Result' : 'Results' }}
              </CBadge>
            </div>
          </CCardHeader>
          <CCollapse :show="filtersExpanded" :duration="400">
            <CCardBody class="filter-body">
              <!-- Price Filters Section -->
              <div class="filter-section">
                <h6 class="filter-section-title">
                  <i class="fas fa-dollar-sign me-2"></i>
                  Price Range Filters
                </h6>
                <CRow class="g-3">
                  <!-- Local Price (MYR) -->
                  <CCol md="4" cols="12">
                    <CInput
                      label="Local Price Min (MYR)"
                      v-model="localPriceMin"
                      type="number"
                      placeholder="e.g. 20000"
                    />
                  </CCol>
                  <CCol md="4" cols="12">
                    <CInput
                      label="Local Price Max (MYR)"
                      v-model="localPriceMax"
                      type="number"
                      placeholder="e.g. 100000"
                    />
                  </CCol>

                  <!-- Foreign Price -->
                  <CCol md="4" cols="12">
                    <CInput
                      label="Foreign Price Min"
                      v-model="foreignPriceMin"
                      type="number"
                      placeholder="e.g. 500000"
                    />
                  </CCol>
                  <CCol md="4" cols="12">
                    <CInput
                      label="Foreign Price Max"
                      v-model="foreignPriceMax"
                      type="number"
                      placeholder="e.g. 2000000"
                    />
                  </CCol>

                  <!-- Body Price (MYR) -->
                  <CCol md="4" cols="12">
                    <CInput
                      label="Body Price Min (MYR)"
                      v-model="bodyPriceMin"
                      type="number"
                      placeholder="e.g. 5000"
                    />
                  </CCol>
                  <CCol md="4" cols="12">
                    <CInput
                      label="Body Price Max (MYR)"
                      v-model="bodyPriceMax"
                      type="number"
                      placeholder="e.g. 50000"
                    />
                  </CCol>
                </CRow>
              </div>

              <!-- Filter Actions -->
              <div class="filter-actions">
                <CButton
                  color="secondary"
                  variant="outline"
                  @click="onCleanerClick"
                  class="me-2"
                >
                  <i class="fas fa-times me-2"></i>
                  Clear
                </CButton>
                <CButton
                  color="primary"
                  @click="refreshTable"
                  :disabled="loading"
                >
                  <i class="fas fa-search me-2"></i>
                  {{ loading ? 'Searching...' : 'Search' }}
                </CButton>
              </div>
            </CCardBody>
          </CCollapse>
        </CCard>
      </CCol>
    </CRow>

    <!-- Main Content -->
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader class="professional-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="header-title">
                <h5 class="mb-0">
                  <i class="fas fa-clipboard-check me-2"></i>
                  <strong>Stock To Buy List</strong>
                  <span
                    class="tooltip-icon ms-2"
                    v-c-tooltip.hover="{
                      content: 'This table shows all stocks to buy. Click on a row to view more details.',
                    }"
                  >
                    <i class="fas fa-info-circle text-muted"></i>
                  </span>
                </h5>
                <small class="text-muted">Manage stocks pending purchase confirmation</small>
              </div>
              <div class="header-actions">
                <CBadge color="info" class="result-counter">
                  {{ computedItems.length }} {{ computedItems.length === 1 ? 'Stock' : 'Stocks' }}
                </CBadge>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Loading State -->
            <div v-if="loading && computedItems.length === 0" class="loading-state">
              <div class="text-center py-5">
                <CSpinner color="primary" />
                <p class="text-muted mt-3">Loading stocks...</p>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && computedItems.length === 0" class="empty-state">
              <div class="text-center py-5">
                <i class="fas fa-clipboard-check fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No stocks to buy found</h5>
                <p class="text-muted">Get started by adding your first stock</p>
                <CButton
                  v-if="canAdd"
                  color="primary"
                  @click="addNew"
                  class="mt-2"
                >
                  <i class="fas fa-plus me-2"></i>
                  Add New Stock
                </CButton>
              </div>
            </div>

            <!-- Data View (Mobile + Desktop) -->
            <div v-else>
            <!-- Mobile Card View -->
            <div class="mobile-view d-md-none">
              <div v-for="(item, index) in computedItems" :key="item.id" class="mobile-card">
                <div class="mobile-card-header" @click="handleRowClick(item, index, $event)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="stock-number text-muted mb-1">
                        <small>#{{ index + 1 }}</small>
                      </div>
                      <h6 class="stock-name mb-1">
                        <i class="fas fa-car me-2 text-primary"></i>
                        {{ item.stockNo }}
                      </h6>
                      <div class="stock-details">
                        <div v-if="item.vehicleDescription" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-info-circle me-1"></i>
                            {{ item.vehicleDescription }}
                          </small>
                        </div>
                        <div v-if="item.color" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-palette me-1"></i>
                            {{ item.color }}
                          </small>
                        </div>
                        <div v-if="item.subCompanyName" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-building me-1"></i>
                            AP: {{ item.subCompanyName }}
                          </small>
                        </div>
                        <div v-if="item.supplierName" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-handshake me-1"></i>
                            Supplier: {{ item.supplierName }}
                          </small>
                        </div>
                        <div class="mt-2">
                          <div v-if="item.localPrice && item.localPrice !== 'N/A'" class="text-success fw-bold mb-1">
                            <small>
                              <i class="fas fa-tag me-1"></i>
                              Local: {{ item.localPrice }}
                            </small>
                          </div>
                          <div v-if="item.foreignPrice && item.foreignPrice !== 'N/A'" class="text-info mb-1">
                            <small>
                              <i class="fas fa-tag me-1"></i>
                              Foreign: {{ item.foreignPrice }}
                            </small>
                          </div>
                          <div v-if="item.bodyPrice && item.bodyPrice !== 'N/A'" class="text-warning">
                            <small>
                              <i class="fas fa-tag me-1"></i>
                              Body: {{ item.bodyPrice }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mobile-card-footer" @click="handleRowClick(item, index, $event)">
                  <small class="text-muted">
                    <i :class="item._toggled ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="me-1"></i>
                    Tap to {{ item._toggled ? 'collapse' : 'view details & actions' }}
                  </small>
                </div>

                <!-- Collapsible Actions -->
                <CCollapse :show="Boolean(item._toggled)" :duration="300">
                  <div class="mobile-card-body">
                    <img
                      v-if="getImage(item)"
                      :src="getImage(item)"
                      alt="Car Image"
                      class="stock-image mb-3"
                    />
                    <div class="d-flex gap-2 flex-wrap">
                      <CButton
                        size="sm"
                        :color="canUpdate ? 'primary' : 'info'"
                        variant="outline"
                        @click="onEdit(item)"
                        class="flex-grow-1"
                      >
                        <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                        {{ canUpdate ? 'Edit' : 'View' }}
                      </CButton>
                      <CButton
                        v-if="canUpdate"
                        size="sm"
                        color="success"
                        variant="outline"
                        @click="showMoveToStockConfirmation(item)"
                        class="flex-grow-1"
                      >
                        <i class="fas fa-arrow-right me-1"></i>
                        Move To Stock
                      </CButton>
                      <CButton
                        v-if="canDelete"
                        size="sm"
                        color="danger"
                        variant="outline"
                        @click="showDeleteConfirmation(item)"
                      >
                        <i class="fas fa-trash me-1"></i>
                        Delete
                      </CButton>
                    </div>
                  </div>
                </CCollapse>
              </div>
            </div>

            <!-- Desktop Table View -->
            <div class="desktop-view d-none d-md-block">
              <div class="table-responsive">
                <CDataTable
                  :items="computedItems"
                  :fields="fields"
                  :column-filter="{ external: true, lazy: true }"
                  :column-filter-value.sync="columnFilterValue"
                  :sorter="{ external: true, resetable: true }"
                  :sorter-value.sync="sorterValue"
                  :items-per-page="itemsPerPage"
                  :active-page="activePage"
                  :hover="true"
                  :loading="loading"
                  @row-clicked="handleRowClick"
                  class="professional-table"
                >
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">#{{ (activePage - 1) * itemsPerPage + index + 1 }}</span>
                    </td>
                  </template>

                  <template #stockNo="{ item }">
                    <td>
                      <div class="d-flex align-items-center">
                        <i class="fas fa-car text-primary me-2"></i>
                        <strong>{{ item.stockNo }}</strong>
                      </div>
                    </td>
                  </template>

                  <template #vehicleDescription="{ item }">
                    <td>
                      <span class="text-muted">{{ item.vehicleDescription || '—' }}</span>
                    </td>
                  </template>

                  <template #color="{ item }">
                    <td>
                      <span v-if="item.color" class="text-muted">
                        <i class="fas fa-palette me-1"></i>
                        {{ item.color }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #subCompanyName="{ item }">
                    <td>
                      <span v-if="item.subCompanyName && item.subCompanyName !== 'N/A'" class="text-muted">
                        <i class="fas fa-building me-1"></i>
                        {{ item.subCompanyName }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #supplierName="{ item }">
                    <td>
                      <span v-if="item.supplierName && item.supplierName !== 'N/A'" class="text-muted">
                        <i class="fas fa-handshake me-1"></i>
                        {{ item.supplierName }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #localPrice="{ item }">
                    <td>
                      <span v-if="item.localPrice && item.localPrice !== 'N/A'" class="text-success fw-bold">
                        {{ item.localPrice }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #foreignPrice="{ item }">
                    <td>
                      <span v-if="item.foreignPrice && item.foreignPrice !== 'N/A'" class="text-info">
                        {{ item.foreignPrice }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #bodyPrice="{ item }">
                    <td>
                      <span v-if="item.bodyPrice && item.bodyPrice !== 'N/A'" class="text-warning">
                        {{ item.bodyPrice }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #details="{ item }">
                    <CCollapse
                      :show="Boolean(item._toggled)"
                      :duration="collapseDuration"
                    >
                      <CCardBody>
                        <p class="text-muted">Stock No: {{ item.stockNo }}</p>
                        <img
                          v-if="getImage(item)"
                          :src="getImage(item)"
                          alt="Car Image"
                          style="max-width: 30%; height: auto; margin-top: 10px"
                        />
                        <div class="button-group">
                          <CButton
                            size="sm"
                            :color="canUpdate ? 'primary' : 'info'"
                            class="ml-1"
                            @click="onEdit(item)"
                          >
                            <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                            {{ canUpdate ? 'Edit' : 'View' }}
                          </CButton>
                          <CButton
                            v-if="canDelete"
                            size="sm"
                            color="danger"
                            class="ml-1"
                            @click="showDeleteConfirmation(item)"
                          >
                            <i class="fas fa-trash me-1"></i>
                            Delete
                          </CButton>
                          <CButton
                            v-if="canUpdate"
                            size="sm"
                            color="primary"
                            class="ml-1"
                            @click="showMoveToStockConfirmation(item)"
                          >
                            <i class="fas fa-arrow-right me-1"></i>
                            Move To Stock
                          </CButton>
                        </div>
                      </CCardBody>
                    </CCollapse>
                  </template>
                </CDataTable>

                <!-- Pagination -->
                <CPagination
                  v-show="pages > 1"
                  :pages="pages"
                  :active-page.sync="activePage"
                  @update:active-page="refreshTable"
                />
              </div>
            </div>
            </div>
          </CCardBody>

          <!-- Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <CButton v-if="canAdd" color="primary" @click="addNew">
                  <i class="fas fa-plus me-2"></i>
                  Add New Stock
                </CButton>
              </div>
              <div class="footer-info">
                <small class="text-muted">
                  Page {{ activePage }} of {{ pages }} • Total: {{ computedItems.length }} {{ computedItems.length === 1 ? 'stock' : 'stocks' }}
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Delete Confirmation Modal -->
    <CModal :show.sync="warningModal" color="warning">
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-exclamation-triangle me-2"></i>
          Confirm Delete
        </h5>
      </template>
      <p>Are you sure you want to delete stock <strong>{{ itemToDelete.stockNo }}</strong>?</p>
      <p class="text-muted mb-0">
        <small>This action cannot be undone.</small>
      </p>
      <template #footer>
        <CButton color="secondary" @click="warningModal = false">
          Cancel
        </CButton>
        <CButton color="danger" @click="onDeleteConfirmation">
          <i class="fas fa-trash me-2"></i>
          Delete
        </CButton>
      </template>
    </CModal>

    <!-- Move To Stock Confirmation Modal -->
    <CModal :show.sync="warningMoveToStockModal" color="primary">
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-arrow-right me-2"></i>
          Confirm Move
        </h5>
      </template>
      <p>Are you sure you want to move stock <strong>{{ itemToMoveToStock.stockNo }}</strong> to Stock?</p>
      <p class="text-muted mb-0">
        <small>This will transfer the item to the main stock inventory.</small>
      </p>
      <template #footer>
        <CButton color="secondary" @click="warningMoveToStockModal = false">
          Cancel
        </CButton>
        <CButton color="primary" @click="onMoveToStockConfirmation">
          <i class="fas fa-arrow-right me-2"></i>
          Move To Stock
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import StockToBuyApi from "../../lib/stockToBuyApi";

export default {
  name: "ConfirmBuyList",
  data() {
    return {
      loadedItems: [],
      columnFilterValue: {},
      tableFilterValue: "",
      sorterValue: { column: null, asc: true },

      // Pagination
      activePage: 1,
      itemsPerPage: 20,
      pages: 1,

      // Price filters
      localPriceMin: "",
      localPriceMax: "",
      foreignPriceMin: "",
      foreignPriceMax: "",
      bodyPriceMin: "",
      bodyPriceMax: "",

      // Filter state
      filtersExpanded: false,

      loading: true,
      infoList: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "stockNo",
          label: "Stock No",
          _style: "min-width:130px;"
        },
        {
          key: "vehicleDescription",
          label: "Description",
          _style: "min-width:200px;",
          sorter: false
        },
        {
          key: "color",
          label: "Color",
          _style: "min-width:100px;"
        },
        {
          key: "subCompanyName",
          label: "AP Company",
          _style: "min-width:150px;"
        },
        {
          key: "supplierName",
          label: "Supplier Company",
          _style: "min-width:150px;"
        },
        {
          key: "localPrice",
          label: "Price (MYR)",
          _style: "min-width:130px;",
          filter: false
        },
        {
          key: "foreignPrice",
          label: "Price (Foreign)",
          _style: "min-width:150px;",
          filter: false
        },
        {
          key: "bodyPrice",
          label: "Body Price (MYR)",
          _style: "min-width:140px;",
          filter: false
        },
      ],
      collapseDuration: 0,
      api: new StockToBuyApi(),
      warningModal: false,
      itemToDelete: {},
      itemToMoveToStock: {},
      warningMoveToStockModal: false,
    };
  },
  mounted() {
    // Check view permission
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }
    console.log('ConfirmBuyList mounted - API URL:', apiUrl);
    console.log('StockToBuyApi instance:', this.api);
    this.refreshTable();
  },
  watch: {
    reloadParams() {
      this.refreshTable();
    },
  },
  computed: {
    reloadParams() {
      return [
        this.sorterValue,
        this.columnFilterValue,
        this.tableFilterValue,
        this.activePage,
        this.itemsPerPage,
        this.localPriceMin,
        this.localPriceMax,
        this.foreignPriceMin,
        this.foreignPriceMax,
        this.bodyPriceMin,
        this.bodyPriceMax,
      ];
    },
    computedItems() {
      console.log('computedItems being calculated. loadedItems:', this.loadedItems);
      const result = this.loadedItems.map((item) => {
        const computed = {
          ...item,
          subCompanyName: this.getSubCompanyName(item),
          supplierName: this.getSupplierName(item),
          localPrice: this.getLocalPricing(item),
          foreignPrice: this.getForeignPricing(item),
          bodyPrice: this.getBodyPricing(item),
          vehicleDescription: this.getVehicleDescription(item),
        };
        console.log('Computed item:', computed);
        return computed;
      });
      console.log('computedItems result:', result);
      return result;
    },
    canView() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanView');
    },
    canAdd() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanUpdate');
    },
    canDelete() {
      return this.$auth.hasModulePermission('STOCK_TO_BUY', 'CanDelete');
    },
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    getForeignPricing(item) {
      if (!item.vehiclePriceSupplierCurrency || item.vehiclePriceSupplierCurrency === 0) return "N/A";

      const symbol = item.supplierCurrency || "";
      const formatted = item.vehiclePriceSupplierCurrency.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return `${symbol} ${formatted}`;
    },

    getLocalPricing(item) {
      if (item.vehiclePriceLocalCurrency == null) return "N/A";
      if (item.vehiclePriceLocalCurrency == 0) return "N/A";
      return new Intl.NumberFormat("ms-MY", {
        style: "currency",
        currency: "MYR",
      }).format(item.vehiclePriceLocalCurrency);
    },

    getBodyPricing(item) {
      if (item.bodyPriceLocalCurrency == null) return "N/A";
      if (item.bodyPriceLocalCurrency == 0) return "N/A";
      return new Intl.NumberFormat("ms-MY", {
        style: "currency",
        currency: "MYR",
      }).format(item.bodyPriceLocalCurrency);
    },

    getSupplierName(item) {
      try {
        return item.supplier.name;
      } catch (error) {
        return "N/A";
      }
    },

    getSubCompanyName(item) {
      try {
        return item.subCompany.name;
      } catch (error) {
        return "N/A";
      }
    },

    getVehicleDescription(item) {
      try {
        return item.description;
      } catch (error) {
        return "";
      }
    },

    getImage(item) {
      if (item.vehicle == null) return "";
      if (item.vehicle.vehiclePhotoList == null) return "";
      if (item.vehicle.vehiclePhotoList.length == 0) return "";
      const url = apiUrl + "Documents/File/" + item.vehicle.vehiclePhotoList[0].documentId;
      return url;
    },

    handleRowClick(item, index, event) {
      this.toggleDetails(item, index);
    },

    toggleDetails(item, index) {
      // Find the actual item in loadedItems by ID to ensure reactivity
      const actualItem = this.loadedItems.find(i => i.id === item.id);

      if (actualItem) {
        this.$set(actualItem, "_toggled", !actualItem._toggled);
      }

      this.collapseDuration = 300;
      this.$nextTick(() => {
        this.collapseDuration = 0;
      });
    },

    onCleanerClick() {
      this.columnFilterValue = {};
      this.tableFilterValue = "";
      this.clearPriceFilters();
      this.refreshTable();
    },

    clearPriceFilters() {
      this.localPriceMin = "";
      this.localPriceMax = "";
      this.foreignPriceMin = "";
      this.foreignPriceMax = "";
      this.bodyPriceMin = "";
      this.bodyPriceMax = "";
    },

    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    refreshTable() {
      this.loading = true;

      // Build filters object with price ranges
      const filters = { ...this.columnFilterValue };

      if (this.localPriceMin) filters.localPriceMin = this.localPriceMin;
      if (this.localPriceMax) filters.localPriceMax = this.localPriceMax;
      if (this.foreignPriceMin) filters.foreignPriceMin = this.foreignPriceMin;
      if (this.foreignPriceMax) filters.foreignPriceMax = this.foreignPriceMax;
      if (this.bodyPriceMin) filters.bodyPriceMin = this.bodyPriceMin;
      if (this.bodyPriceMax) filters.bodyPriceMax = this.bodyPriceMax;

      const params = {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        sorter: this.sorterValue,
        filters: filters,
        search: this.tableFilterValue,
      };

      console.log('ConfirmBuyList refreshTable params:', params);

      this.api
        .getList(params)
        .then((response) => {
          console.log('ConfirmBuyList API Response:', response);
          console.log('Response.result:', response.result);

          if (response.result) {
            this.loadedItems = response.result.items || [];
            console.log('Loaded Items:', this.loadedItems);
            console.log('Items Count:', this.loadedItems.length);
            console.log('Total Items:', response.result.totalItems);

            this.pages = Math.ceil(
              response.result.totalItems / this.itemsPerPage
            );
          } else {
            console.warn('No response.result found in API response');
            this.loadedItems = [];
            this.pages = 1;
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error('ConfirmBuyList API Error:', error);
          console.error('Error details:', error.response);
          this.toast("Error", "Failed to load stocks", "danger");
          this.loading = false;
        });
    },

    onEdit(item) {
      this.$router.push({
        path: `/admins/ConfirmBuy/${item.id}`,
      });
    },

    addNew() {
      this.$router.push({ path: "/admins/ConfirmBuy" });
    },

    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },

    onDeleteConfirmation() {
      this.api
        .delete(this.itemToDelete.id)
        .then((response) => {
          this.refreshTable();
          this.toast("Success", "Stock deleted successfully", "success");
          this.warningModal = false;
          this.itemToDelete = {};
        })
        .catch((error) => {
          this.toast("Error", "Failed to delete stock", "danger");
          this.warningModal = false;
        });
    },

    showMoveToStockConfirmation(item) {
      this.itemToMoveToStock = item;
      this.warningMoveToStockModal = true;
    },

    onMoveToStockConfirmation() {
      this.api
        .moveToStockConfirmation(this.itemToMoveToStock.id)
        .then((response) => {
          this.refreshTable();
          this.toast("Success", "Stock moved successfully", "success");
          this.warningMoveToStockModal = false;
          this.itemToMoveToStock = {};
        })
        .catch((error) => {
          this.toast("Error", "Failed to move stock", "danger");
          this.warningMoveToStockModal = false;
        });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.confirm-buy-list-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Filter Card Styling */
.filter-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.filter-header {
  background-color: var(--cui-body-bg, #f8f9fa);
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: #e9ecef;
}

.filter-header .header-content {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
}

.filter-header .header-content i.fas {
  font-size: 1rem;
}

.filter-count {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
}

.filter-body {
  padding: 1.5rem 1.25rem;
}

/* Filter Sections */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-section:last-of-type {
  margin-bottom: 0;
}

.filter-section-title {
  color: #4f5d73;
  font-size: 0.9375rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
}

.filter-section-title i {
  color: #768192;
  font-size: 0.875rem;
}

/* Filter Actions */
.filter-actions {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filter-actions .btn {
  min-width: 100px;
  font-weight: 500;
}

.filter-actions .btn i {
  font-size: 0.875rem;
}

/* Main Card */
.main-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Header */
.professional-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1.25rem 1.5rem;
}

.header-title h5 {
  font-size: 1.25rem;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.25rem;
}

.header-title small {
  font-size: 0.875rem;
}

.tooltip-icon {
  cursor: pointer;
}

.result-counter {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

/* Mobile Card View */
.mobile-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: white;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

.mobile-card-header {
  padding: 1rem 1.25rem;
  cursor: pointer;
  background: white;
}

.mobile-card-header:hover {
  background: var(--cui-body-bg, #f8f9fa);
}

.mobile-card-footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--cui-border-color-translucent, rgba(0, 0, 0, 0.125));
  text-align: center;
  background: white;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.mobile-card-footer:hover {
  background: var(--cui-body-bg, #f8f9fa);
}

.mobile-card-footer small {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.stock-number {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stock-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--cui-body-color, #212529);
}

.stock-details {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.mobile-card-body {
  padding: 1rem 1.25rem;
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

.stock-image {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Desktop Table View */
.desktop-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.professional-table tbody tr:hover {
  background-color: var(--cui-table-hover-bg, rgba(0, 0, 0, 0.075));
}

.button-group {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Footer */
.card-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.5rem;
}

.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .confirm-buy-list-wrapper {
    padding: 1rem;
  }

  .filter-header {
    padding: 0.875rem 1rem;
  }

  .filter-header .header-content {
    font-size: 1rem;
  }

  .filter-body {
    padding: 1rem;
  }

  .filter-section {
    margin-bottom: 1.25rem;
  }

  .filter-section-title {
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
  }

  .filter-actions {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .filter-actions .btn {
    width: 100%;
  }

  .professional-header {
    padding: 1rem;
  }

  .header-title h5 {
    font-size: 1.125rem;
  }

  .card-footer {
    padding: 1rem;
  }

  .footer-actions {
    width: 100%;
  }

  .footer-actions .btn {
    flex: 1;
  }
}

/* Tablet Responsiveness */
@media (min-width: 768px) and (max-width: 991.98px) {
  .filter-body {
    padding: 1.25rem 1rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .filter-card,
  .card-footer,
  .mobile-card-body,
  .button-group {
    display: none !important;
  }

  .mobile-view {
    display: none !important;
  }

  .desktop-view {
    display: block !important;
  }
}
</style>
